// Module dependencies
import Logging from 'app/common/lib/Logging';
import Pc from 'app/common/lib/Pc';
import pubSub from 'app/common/lib/PubSub';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import user from 'app/common/lib/User';

class ExperienceManager {
  /**
   * Construct a new experience manager that is responsible for rendering
   * user experiences on placements on the client.
   *
   * @constructor
   * @param {!PlacedExperiences} placedExperiences
   */
  constructor(placedExperiences) {
    this._placedExperiences = placedExperiences;
    this._triggered = new Set();
  }

  /**
   * Get a resource.  Also, a hook for tests.
   *
   * @private
   * @param {string} name
   * @param {!Object.<string,*>=} options
   * @return {!IResource}
   */
  _getResource(name, options) {
    return ResourceFactory.create(name, options);
  }

  /**
   * A helper function to construct a placed experience id.  This is necessary for
   * referring to contextual placed experiences.
   */
  constructPlacedExperienceId(placementId, experienceId) {
    return placementId && experienceId ? `${placementId}:${experienceId}` : null;
  }

  /**
   * Check if a given placementId and experienceId is a placed experience.
   * Also reports the experiment as triggered if it hasn't been triggered already.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @param {PCONST.thrift.Experience} experienceId
   * @return {boolean} true or false indicating whether the placed experience exists.
   */
  isPlacedExperience(placementId, experienceId) {
    const placedExperience = this.getPlacedExperience(placementId);
    if (placedExperience) {
      this.triggerPlacedExperienceIds(placedExperience.triggerable_placed_exps);
      return placedExperience.experience_id === experienceId;
    }
    return false;
  }

  /**
   * Check if a given placementId has any experience.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @return {boolean} true if there is an experience that's available to be placed.
   */
  isPlaced(placementId) {
    return this._placedExperiences[placementId] !== undefined;
  }

  /**
   * Retrieve the placed experience id given a placementId and
   * experienceId.  null is returned if no such placed experience
   * exists.
   *
   * @private
   * @param {PCONST.thrift.Placement} placementId
   * @param {PCONST.thrift.Experience} experienceId
   * @return {?string} the placed experience id for a given placementId and
   * experienceId.
   */
  getPlacedExperienceId(placementId, experienceId) {
    if (placementId && experienceId) {
      const placedExperience = this._placedExperiences[placementId];
      if (placedExperience && placedExperience.experience_id === experienceId) {
        return placedExperience.id;
      }
    }
    return null;
  }

  /**
   * Retrieve the placed experience associated with a given placementId.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @param {!Object.<string, *>=} extraContext extra data used to evaluate if user should be in experience
   * @param {boolean} noTrigger if true, do not trigger the triggerable_placed_exps immediately
   *                  NOTE: ONLY USE IF YOU KNOW WHAT YOU ARE DOING. You will cause imbalance in
   *                  other people's experiments if you don't call triggerPlacedExperienceIds.
   * @return {!(Promise.<!PlacedExperience>|Promise.<null>)} the placed experience associated with the placementId.
   */
  getPlacedExperienceWithContext(placementId, extraContext, noTrigger) {
    // Null check
    if (!placementId) {
      return Promise.resolve(null);
    }

    const placedExperience = this.getPlacedExperience(placementId);
    if (this.isPlacedEligibleForContextualCall(placedExperience, extraContext)) {
      const resource = this._getResource('UserExperienceResource', {
        placement_ids: [placementId],
        extra_context: extraContext,
      });
      return resource.callGet({ showError: false }).then((resourceResponse) => {
        const placedExperiences = resourceResponse.resource_response.data || {};
        const placedExperience = placedExperiences[placementId];
        if (placedExperience) {
          if (!noTrigger) {
            this.triggerPlacedExperienceIds(placedExperience.triggerable_placed_exps, extraContext);
          }
          return placedExperience;
        }
        return null;
      });
    } else if (placedExperience) {
      if (!noTrigger) {
        this.triggerPlacedExperienceIds(placedExperience.triggerable_placed_exps, extraContext);
      }
      return Promise.resolve(placedExperience);
    } else {
      return Promise.resolve(null);
    }
  }

  isPlacedEligibleForContextualCall(placedExperience, extraContext) {
    // no placed experience or noop experience
    if (placedExperience && placedExperience.experience_id !== PCONST.thrift.Experience.NOOP) {
      return false;
    }
    // extraContext is not defined
    if (!extraContext) {
      return false;
    }
    // extraContext is empty
    if (!Object.keys(extraContext).length) {
      return false;
    }

    return true;
  }

  /**
   * Retrieve the placed experience associated with a given placementId.
   *
   * @param {PCONST.thrift.Placement} placementId
   *
   * @return {?PlacedExperience} the placed experience associated with the placementId.
   */
  getPlacedExperience(placementId) {
    return (this._placedExperiences && this._placedExperiences[placementId]) || null;
  }

  /**
   * Retrieve the placed experience type associated with a given placementId.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @return {?PCONST.thrift.ExperienceType} the type of the placed experience
   */
  getPlacedExperienceType(placementId) {
    const placedExperience = this.getPlacedExperience(placementId);
    return placedExperience ? placedExperience.type : null;
  }

  /**
   * Refreshes all the placed experience in the client by making a call
   * to the backend.  This should be done whenever we think an action will trigger
   * experience changes.
   * TODO(chris): Remove manual refreshing of experiments in favor of automatically
   * syncing experiences.
   */
  refreshPlacedExperiences() {
    ExperienceManager.logger.debug('Refreshing all placed experiences.');
    const resource = this._getResource('UserExperiencePlatformResource');
    return resource.callGet({ showError: false }).then((resourceResponse) => {
      const placedExperiences /** @type {!PlacedExperiences} */ =
        resourceResponse.resource_response.data || {};
      pubSub.instance.publish(
        PCONST.PUBSUB_CHANNEL_SITE,
        PCONST.PUBSUB_TOPIC_PLACED_EXPERIENCES_FROM_SERVER,
        placedExperiences,
      );
    });
  }

  /**
   * Dismiss a placed experience.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @param {PCONST.thrift.Experience} experienceId
   * @param {boolean} async
   * @return {!Promise.<undefined>}
   */
  dismissPlacedExperience(placementId, experienceId, async) {
    const placedExperienceId = this.getPlacedExperienceId(placementId, experienceId);
    if (placedExperienceId) {
      delete this._placedExperiences[placementId];
      return this.dismissPlacedExperienceId(placedExperienceId, async);
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Dismiss a placed experience given a placedExperienceId.
   *
   * @param {string} placedExperienceId the placed experience id.
   * @param {boolean} async whether the dismissal is asynchronous
   * @return {!Promise.<undefined>}
   */
  dismissPlacedExperienceId(placedExperienceId, async) {
    if (!user.isAuthenticated()) {
      return Promise.reject(new Error('dismissPlacedExperienceId called with unauth user'));
    }
    if (placedExperienceId) {
      ExperienceManager.logger.debug('Delete placed experience: ', placedExperienceId);

      const resourceOptions = {
        placed_experience_id: placedExperienceId,
      };
      if (async !== undefined) {
        resourceOptions.async = async;
      }

      /** @type {!IResource} */
      const resource = this._getResource('UserExperienceResource', resourceOptions);
      return resource.callDelete({ showError: false }).then(() => this.refreshPlacedExperiences());
    } else {
      return Promise.resolve();
    }
  }

  /**
   * View a placed experience.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @param {PCONST.thrift.Experience} experienceId
   */
  viewPlacedExperience(placementId, experienceId) {
    const placedExperienceId = this.getPlacedExperienceId(placementId, experienceId);

    return this.viewPlacedExperienceId(placedExperienceId);
  }

  /**
   * Report that a placed experience was triggered and mark the
   * placement as triggered so that we don't trigger it more than once
   * per call to #Module.render.
   *
   * @private
   * @param {string} placedExperienceId
   * @param {!Object=} extraContext
   */
  triggerPlacedExperienceId(placedExperienceId, extraContext) {
    if (!user.isAuthenticated()) {
      return Promise.reject(new Error('triggerPlacedExperienceId called with unauth user'));
    }
    if (!this._triggered.has(placedExperienceId)) {
      this._triggered.add(placedExperienceId);

      let newExtraContext = extraContext;

      // We need to split the placedExperienceId into placementId and experienceId
      const splitId = placedExperienceId.split(':');
      const placementId = parseInt(splitId[0], 10);
      const experienceId = parseInt(splitId[1], 10);
      const experience = this._placedExperiences[placementId] || {};
      if (experience.metadata && experience.metadata[experienceId]) {
        newExtraContext = Object.assign({}, extraContext, experience.metadata[experienceId]);
      }

      const resource = this._getResource('UserExperienceTriggerResource', {
        placed_experience_id: placedExperienceId,
        extra_context: newExtraContext,
      });
      return resource.callUpdate({ showError: false });
    }
    return Promise.resolve();
  }

  /**
   * @param {!Array.<string>} placedExperienceIds
   * @param {!Object=} extraContext
   */
  triggerPlacedExperienceIds(placedExperienceIds, extraContext) {
    // TODO(chris): These should be sent in a batch instead of being individual resource calls.
    if (placedExperienceIds) {
      return Promise.all(
        placedExperienceIds.map((placedExperienceId) =>
          this.triggerPlacedExperienceId(placedExperienceId, extraContext),
        ),
      );
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Report that a placed experience was viewed.
   *
   * @param {string} placedExperienceId the placed experience id.
   */
  viewPlacedExperienceId(placedExperienceId) {
    if (!user.isAuthenticated()) {
      return Promise.reject(new Error('viewPlacedExperienceId called with unauth user'));
    }
    if (placedExperienceId) {
      ExperienceManager.logger.debug('Viewed placed experience: ', placedExperienceId);

      const resource = this._getResource('UserExperienceViewedResource', {
        placed_experience_id: placedExperienceId,
      });
      // For performance reasons we don't refresh placements on view.
      return resource.callUpdate({ showError: false });
    } else {
      return Promise.reject(new Error('viewPlacedExperienceId called with no id'));
    }
  }

  /**
   * Complete a placed experience.
   *
   * @param {PCONST.thrift.Placement} placementId
   * @param {PCONST.thrift.Experience} experienceId
   * @return {!Promise.<undefined>}
   */
  completePlacedExperience(placementId, experienceId) {
    const placedExperienceId = this.getPlacedExperienceId(placementId, experienceId);
    if (placedExperienceId) {
      delete this._placedExperiences[placementId];
      return this.completePlacedExperienceId(placedExperienceId);
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Complete a placed experience for a given placed experience id.
   *
   * @param {string} placedExperienceId the placed experience id.
   * @return {!Promise.<undefined>}
   */
  completePlacedExperienceId(placedExperienceId) {
    if (!user.isAuthenticated()) {
      return Promise.reject(new Error('completePlacedExperienceId called with unauth user'));
    }
    if (placedExperienceId) {
      ExperienceManager.logger.debug('Complete placed experience: ', placedExperienceId);

      const resource = this._getResource('UserExperienceCompletedResource', {
        placed_experience_id: placedExperienceId,
      });
      return resource.callUpdate({ showError: false }).then(() => this.refreshPlacedExperiences());
    } else {
      return Promise.resolve();
    }
  }

  setPlacedExperiences(placedExperiences) {
    this._placedExperiences = placedExperiences;
  }

  clearPlacedExperiences() {
    this._placedExperiences = {};
  }

  static instance = new ExperienceManager(Pc.expns);
  static logger = Logging.getLogger('ExperienceManager');
  static refreshSubscription = pubSub.instance.subscribe(
    PCONST.PUBSUB_CHANNEL_SITE,
    PCONST.PUBSUB_TOPIC_PLACED_EXPERIENCES_FROM_SERVER,
    /**
     * @param {!PlacedExperiences} placedExperiences
     */
    (channel, topic, placedExperiences) => {
      if (placedExperiences) {
        ExperienceManager.instance.setPlacedExperiences(placedExperiences);
        Pc.expns = placedExperiences;
      }
    },
  );
}

export default ExperienceManager;
